import { createAction, createActionGroup, emptyProps, props } from '@ngrx/store';

import {
  GetResponse,
  Invitation,
  InvitationAccessLevel,
  CommercialInvitation,
  InvitationInstallationDetails,
  InvitationOwnerDetails,
  InvitationsQueryParams,
  UpdateInvitationRequest,
  InvitationStatusType,
} from '@vi-iot/client';

import {
  ActiveInvitationsFilterType,
  BulkInvitations,
  BulkSaveBuildingMangerInvitationsPayload,
  InstallationInvitationToCreate,
  InstallationInvitationToUpdate,
} from './invitations.model';

export namespace InvitationsActions {
  export enum Type {
    LIST_INSTALLATION_INVITATIONS = '[INVITATIONS] List Installation Invitations',
    BULK_SAVE_BUILDING_MANAGER_INVITATIONS = '[INVITATIONS] Bulk Save Building Manager Invitations',
    BULK_SAVE_BUILDING_MANAGER_INVITATIONS_SUCCESS = '[INVITATIONS] Bulk Save Building Manager Invitations Success',
    BULK_SAVE_BUILDING_MANAGER_INVITATIONS_COMPLETE = '[INVITATIONS] Bulk Save Building Manager Invitations Complete',
    BULK_UPDATE_INSTALLATION_INVITATIONS = '[INVITATIONS] Bulk Update Installation Invitations',
    BULK_CREATE_INSTALLTION_INVITATIONS = '[INVITATIONS] Bulk Create Installation Invitations',
    LIST_PENDING_TRANSFER_INSTALLATION_INVITATIONS = '[INVITATIONS] List Pending Transfer Installation Invitations',
    LIST_PENDING_TRANSFER_INSTALLATION_INVITATIONS_SUCCESS = '[INVITATIONS] List Pending Transfer Installation Invitations Success',
    LIST_PENDING_TRANSFER_INSTALLATION_INVITATIONS_ERROR = '[INVITATIONS] List Pending Transfer Installation Invitations Error',
    BULK_LIST_INVITATIONS = '[INVITATIONS] Bulk List Invitations',
    BULK_LIST_INVITATIONS_SUCCESS = '[INVITATIONS] Bulk List Invitations Success',
    BULK_LIST_INVITATIONS_ERROR = '[INVITATIONS] Bulk List Invitations Error',
    GET_USER_INVITATIONS = '[INVITATIONS] Get User Invitations',
    GET_USER_INVITATIONS_SUCCESS = '[INVITATIONS] Get User Invitations Success',
    GET_USER_INVITATIONS_ERROR = '[INVITATIONS] Get User Invitations Error',
    GET_INCOMING_INVITATIONS_COUNT = '[INVITATIONS] Get Incoming Invitations Count',
    GET_INCOMING_INVITATIONS_COUNT_SUCCESS = '[INVITATIONS] Get Incoming Invitations Count Success',
    GET_INCOMING_INVITATIONS_COUNT_ERROR = '[INVITATIONS] Get Incoming Invitations Count Error',
    UPDATE_INVITATION = '[INVITATIONS] Update Invitation',
    UPDATE_INVITATION_SUCCESS = '[INVITATIONS] Update Invitation Success',
    UPDATE_INVITATION_ERROR = '[INVITATIONS] Update Invitation Error',
    GET_INVITATION_OWNER_DETAILS = '[INVITATIONS] Get Invitation Owner Details',
    GET_INVITATION_OWNER_DETAILS_SUCCESS = '[INVITATIONS] Get Invitation Owner Details Success',
    GET_INVITATION_OWNER_DETAILS_ERROR = '[INVITATIONS] Get Invitation Owner Details Error',
    GET_INVITATION_INSTALLATION_DETAILS = '[INVITATIONS] Get Invitation Installation Details',
    GET_INVITATION_INSTALLATION_DETAILS_SUCCESS = '[INVITATIONS] Get Invitation Installation Details Success',
    GET_INVITATION_INSTALLATION_DETAILS_ERROR = '[INVITATIONS] Get Invitation Installation Details Error',
    FILTER_INVITATIONS = '[INVITATIONS] Filter Invitations',
    ADD_PENDING_INVITATION = '[INVITATIONS] Add Pending Invitation',
    ACCEPT_INCOMING_INVITATION = '[INVITATIONS] Accept Incoming Invitation',
    REJECT_INCOMING_INVITATION = '[INVITATIONS] Reject Incoming Invitation',
    CANCEL_INVITATION = '[INVITATIONS] Cancel Invitation',
    CREATE_INVITATION = '[INVITATIONS] Create Invitation',
    CREATE_INVITATION_SUCCESS = '[INVITATIONS] Create Invitation Success',
    CREATE_INVITATION_ERROR = '[INVITATIONS] Create Invitation Error',
  }
  export const createInvitation = createAction(
    Type.CREATE_INVITATION,
    props<{
      installationId: number;
      inviteeEmail: string;
      accessLevel: InvitationAccessLevel;
    }>()
  );

  export const bulkSaveBuildingMangerInvitations = createAction(
    Type.BULK_SAVE_BUILDING_MANAGER_INVITATIONS,
    props<{
      invitationsToSave: BulkSaveBuildingMangerInvitationsPayload;
    }>()
  );

  export const bulkSaveBuildingMangerInvitationsSuccess = createAction(
    Type.BULK_SAVE_BUILDING_MANAGER_INVITATIONS_SUCCESS
  );

  export const bulkSaveBuildingMangerInvitationsComplete = createAction(
    Type.BULK_SAVE_BUILDING_MANAGER_INVITATIONS_COMPLETE
  );

  export const bulkUpdateInstallationInvitations = createAction(
    Type.BULK_UPDATE_INSTALLATION_INVITATIONS,
    props<{
      installationId: string | number;
      invitationsToUpdate: InstallationInvitationToUpdate[];
      invitationsToCreate?: InstallationInvitationToCreate[];
    }>()
  );

  export const bulkCreateInstallationInvitations = createAction(
    Type.BULK_CREATE_INSTALLTION_INVITATIONS,
    props<{
      installationId: string | number;
      invitationsToCreate: InstallationInvitationToCreate[];
    }>()
  );

  export const createInvitationSuccess = createAction(
    Type.CREATE_INVITATION_SUCCESS,
    props<{
      response: Invitation;
    }>()
  );

  export const createInvitationError = createAction(
    Type.CREATE_INVITATION_ERROR,
    props<{ error: any }>()
  );

  export const getIncomingInvitationsCount = createAction(Type.GET_INCOMING_INVITATIONS_COUNT);

  export const getIncomingInvitationsCountSuccess = createAction(
    Type.GET_INCOMING_INVITATIONS_COUNT_SUCCESS,
    props<{ count: number }>()
  );

  export const getIncomingInvitationsCountError = createAction(
    Type.GET_INCOMING_INVITATIONS_COUNT_ERROR
  );

  export const listInstallationInvitations = createAction(
    Type.LIST_INSTALLATION_INVITATIONS,
    props<{
      installationId?: string | number;
    }>()
  );

  export const listPendingTransferInstallationInvitations = createAction(
    Type.LIST_PENDING_TRANSFER_INSTALLATION_INVITATIONS,
    props<{
      installationId?: string | number;
    }>()
  );

  export const listPendingTransferInstallationInvitationsSuccess = createAction(
    Type.LIST_PENDING_TRANSFER_INSTALLATION_INVITATIONS_SUCCESS,
    props<{
      pendingInvitations: Invitation[];
    }>()
  );

  export const listPendingTransferInstallationInvitationsError = createAction(
    Type.LIST_PENDING_TRANSFER_INSTALLATION_INVITATIONS_ERROR
  );

  export const bulkListInvitations = createAction(
    Type.BULK_LIST_INVITATIONS,
    props<{ installationId?: string | number }>()
  );

  export const bulkListInvitationsSuccess = createAction(
    Type.BULK_LIST_INVITATIONS_SUCCESS,
    props<{
      invitations: BulkInvitations;
      buildingManagerInvitations: Invitation[];
    }>()
  );

  export const bulkListInvitationsError = createAction(Type.BULK_LIST_INVITATIONS_ERROR);

  export const getUserInvitations = createAction(
    Type.GET_USER_INVITATIONS,
    props<{
      params: InvitationsQueryParams;
    }>()
  );

  export const getUserInvitationsSuccess = createAction(
    Type.GET_USER_INVITATIONS_SUCCESS,
    props<{
      data: Invitation[];
    }>()
  );

  export const getUserInvitationsError = createAction(Type.GET_USER_INVITATIONS_ERROR);

  export const updateInvitation = createAction(
    Type.UPDATE_INVITATION,
    props<{
      token: string;
      request: UpdateInvitationRequest;
      ownerName: string;
    }>()
  );

  export const updateInvitationSuccess = createAction(
    Type.UPDATE_INVITATION_SUCCESS,
    props<{
      response: GetResponse<Invitation>;
      ownerName: string;
    }>()
  );

  export const updateInvitationError = createAction(Type.UPDATE_INVITATION_ERROR);

  export const updateCommercialInvitation = createActionGroup({
    source: 'INVITATIONS Update Commercial Invitation',
    events: {
      attempt: props<{
        token: string;
        request: UpdateInvitationRequest;
        ownerName: string;
      }>(),
      success: props<{
        token: string;
        status: InvitationStatusType;
      }>(),
      failed: emptyProps(),
    },
  });

  export const getCommercialInvitationDetails = createActionGroup({
    source: 'INVITATIONS Get Commercial Details',
    events: {
      attempt: props<{
        token: string;
        installationId: number;
        note: string;
      }>(),
      success: props<{
        token: string;
        data: CommercialInvitation;
      }>(),
      failed: emptyProps(),
    },
  });

  export const getInvitationOwnerDetails = createAction(
    Type.GET_INVITATION_OWNER_DETAILS,
    props<{
      token: string;
    }>()
  );

  export const getInvitationOwnerDetailsSuccess = createAction(
    Type.GET_INVITATION_OWNER_DETAILS_SUCCESS,
    props<{
      token: string;
      response: GetResponse<InvitationOwnerDetails>;
    }>()
  );

  export const getInvitationOwnerDetailsError = createAction(
    Type.GET_INVITATION_OWNER_DETAILS_ERROR
  );

  export const getInvitationInstallationDetails = createAction(
    Type.GET_INVITATION_INSTALLATION_DETAILS,
    props<{
      token: string;
    }>()
  );

  export const getInvitationInstallationDetailsSuccess = createAction(
    Type.GET_INVITATION_INSTALLATION_DETAILS_SUCCESS,
    props<{
      token: string;
      response: GetResponse<InvitationInstallationDetails>;
    }>()
  );

  export const getInvitationInstallationDetailsError = createAction(
    Type.GET_INVITATION_INSTALLATION_DETAILS_ERROR
  );

  export const filterInvitations = createAction(
    Type.FILTER_INVITATIONS,
    props<{
      activeFilter: ActiveInvitationsFilterType;
    }>()
  );

  export const addPendingInvitation = createAction(
    Type.ADD_PENDING_INVITATION,
    props<{
      invitation: Invitation;
      gatewaySerial: string;
    }>()
  );

  export const acceptIncomingInvitation = createAction(
    Type.ACCEPT_INCOMING_INVITATION,
    props<{
      invitation: Invitation;
    }>()
  );

  export const rejectIncomingInvitation = createAction(
    Type.REJECT_INCOMING_INVITATION,
    props<{
      invitation: Invitation;
    }>()
  );

  export const cancelInvitation = createAction(
    Type.CANCEL_INVITATION,
    props<{
      invitation: Invitation;
    }>()
  );

  export const bulkUpdateInstalltionInvitations = createAction(
    Type.BULK_UPDATE_INSTALLATION_INVITATIONS,
    props<{
      installationId: string | number;
      invitationsToUpdate: InstallationInvitationToUpdate[];
      invitationsToCreate?: InstallationInvitationToCreate[];
    }>()
  );
}
